import { useEffect, useMemo, useState } from "react";

import { resolveId } from "@medplum/core";
import { Encounter, Observation, QuestionnaireResponse } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Paper, Stack, Title } from "@mantine/core";

import { Loading } from "@/components/Loading";
import {
  ClientPostpartumFlowsheetForms,
  NewbornPostpartumFlowsheetForms,
  PostpartumClientQuestionnaireType,
  PostpartumNewbornQuestionnaireType,
} from "@/encounters/components/EncounterLabor/constants/postpartum-flowsheets";
import { fetchPostpartumObservationsForDiff } from "@/encounters/components/EncounterLabor/services/labor";
import {
  isObservationPartOfEncounterAmendmentDiff,
  isObservationWithAmendVersionChain,
} from "@/encounters/components/EncounterLabor/services/labor-encounter-amend-observation-filter";
import { laborObservationVersionDiffRowHasVisibleChange } from "@/encounters/components/EncounterLabor/utils/labor-observation-version-diff-change";
import { resolveObservationAmendPredecessor } from "@/encounters/components/EncounterLabor/utils/observation-version-chain";

import {
  collapseObservationRowsToLatestChain,
  ObservationVersionRow,
} from "./collapse-observation-version-chains";
import LaborObservationDiff from "./LaborObservationDiff";

interface PostpartumDiffProps {
  encounter: Encounter;
  /** When both are set, only observations materialized between these encounter QuestionnaireResponse versions are shown. */
  previousEncounterQuestionnaireResponse?: QuestionnaireResponse;
  currentEncounterQuestionnaireResponse?: QuestionnaireResponse;
  questionnaireResponsesById?: Map<string, QuestionnaireResponse>;
  restrictToEncounterAmendmentWindow?: boolean;
  showOnlyObservationVersionChainDiffs?: boolean;
}

function buildPostpartumObservationRows(
  observations: ReadonlyArray<Observation>,
  options: {
    previousEncounterQuestionnaireResponse?: QuestionnaireResponse;
    currentEncounterQuestionnaireResponse?: QuestionnaireResponse;
    questionnaireResponsesById?: Map<string, QuestionnaireResponse>;
    restrictToEncounterAmendmentWindow: boolean;
    showOnlyObservationVersionChainDiffs: boolean;
  },
): { clientRows: ObservationVersionRow[]; newbornRows: ObservationVersionRow[] } {
  const observationsById = new Map(
    observations
      .filter((observation): observation is Observation & { id: string } => Boolean(observation.id))
      .map((observation) => [observation.id, observation]),
  );

  const clientObservationRows: ObservationVersionRow[] = [];
  const newbornObservationRows: ObservationVersionRow[] = [];

  for (const currentObservation of observations) {
    if (options.showOnlyObservationVersionChainDiffs) {
      if (!isObservationWithAmendVersionChain(currentObservation, observationsById)) {
        continue;
      }
    } else if (options.restrictToEncounterAmendmentWindow) {
      if (
        !options.previousEncounterQuestionnaireResponse ||
        !options.currentEncounterQuestionnaireResponse
      ) {
        continue;
      }
      if (
        !isObservationPartOfEncounterAmendmentDiff(
          currentObservation,
          options.previousEncounterQuestionnaireResponse,
          options.currentEncounterQuestionnaireResponse,
          {
            observationsById,
            questionnaireResponsesById: options.questionnaireResponsesById,
          },
        )
      ) {
        continue;
      }
    }

    const previousObservation = resolveObservationAmendPredecessor(
      currentObservation,
      observationsById,
      observations,
    );

    const observationPair = { currentObservation, previousObservation };
    const code = currentObservation.code?.coding?.[0]?.code;

    const isClientForm = Object.values(ClientPostpartumFlowsheetForms).some(
      (formCode) => code?.includes(formCode) || code === formCode,
    );
    const isNewbornForm = Object.values(NewbornPostpartumFlowsheetForms).some(
      (formCode) => code?.includes(formCode) || code === formCode,
    );
    const isClientQuestionnaire = Object.values(PostpartumClientQuestionnaireType).some(
      (questionnaireType) => code === questionnaireType,
    );
    const isNewbornQuestionnaire = Object.values(PostpartumNewbornQuestionnaireType).some(
      (questionnaireType) => code === questionnaireType,
    );

    if (isClientForm || isClientQuestionnaire) {
      clientObservationRows.push(observationPair);
    } else if (isNewbornForm || isNewbornQuestionnaire) {
      newbornObservationRows.push(observationPair);
    } else {
      clientObservationRows.push(observationPair);
    }
  }

  return {
    clientRows: collapseObservationRowsToLatestChain(
      clientObservationRows,
      observationsById,
    ).filter(({ currentObservation, previousObservation }) =>
      laborObservationVersionDiffRowHasVisibleChange(currentObservation, previousObservation),
    ),
    newbornRows: collapseObservationRowsToLatestChain(
      newbornObservationRows,
      observationsById,
    ).filter(({ currentObservation, previousObservation }) =>
      laborObservationVersionDiffRowHasVisibleChange(currentObservation, previousObservation),
    ),
  };
}

const PostpartumDiff = ({
  encounter,
  previousEncounterQuestionnaireResponse,
  currentEncounterQuestionnaireResponse,
  questionnaireResponsesById,
  restrictToEncounterAmendmentWindow = false,
  showOnlyObservationVersionChainDiffs = false,
}: PostpartumDiffProps) => {
  const [postpartumObservations, setPostpartumObservations] = useState<Observation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const medplum = useMedplum();

  const encounterId = encounter.id;
  const clientId = resolveId(encounter.subject);

  useEffect(() => {
    const loadPostpartumObservations = async () => {
      medplum.invalidateSearches("Observation");
      setIsLoading(true);
      try {
        if (!encounterId || !clientId) {
          setPostpartumObservations([]);
          return;
        }

        const observations = await fetchPostpartumObservationsForDiff(
          medplum,
          clientId,
          encounterId,
        );
        setPostpartumObservations(observations);
      } finally {
        setIsLoading(false);
      }
    };
    void loadPostpartumObservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Fetch when encounter inputs change; Medplum client reference is not a stable effect dependency.
  }, [encounterId, clientId]);

  const { clientObservations, newbornObservations } = useMemo(() => {
    const { clientRows, newbornRows } = buildPostpartumObservationRows(postpartumObservations, {
      previousEncounterQuestionnaireResponse,
      currentEncounterQuestionnaireResponse,
      questionnaireResponsesById,
      restrictToEncounterAmendmentWindow,
      showOnlyObservationVersionChainDiffs,
    });
    return { clientObservations: clientRows, newbornObservations: newbornRows };
  }, [
    postpartumObservations,
    previousEncounterQuestionnaireResponse,
    currentEncounterQuestionnaireResponse,
    questionnaireResponsesById,
    restrictToEncounterAmendmentWindow,
    showOnlyObservationVersionChainDiffs,
  ]);

  if (isLoading) return <Loading />;

  if (clientObservations.length === 0 && newbornObservations.length === 0) {
    return null;
  }

  return (
    <Stack gap="lg">
      {clientObservations.length > 0 && (
        <Paper p="md" withBorder>
          <Stack mb="lg">
            <Title order={3}>Client Postpartum Observations</Title>
          </Stack>
          <Stack gap="lg">
            {clientObservations.map(({ currentObservation, previousObservation }) => (
              <LaborObservationDiff
                key={currentObservation.id}
                currentObservation={currentObservation}
                previousObservation={previousObservation}
                title={
                  currentObservation.code?.coding?.[0]?.display || "Client Postpartum Observation"
                }
              />
            ))}
          </Stack>
        </Paper>
      )}

      {newbornObservations.length > 0 && (
        <Paper p="md" withBorder>
          <Stack mb="lg">
            <Title order={3}>Newborn Postpartum Observations</Title>
          </Stack>
          <Stack gap="lg">
            {newbornObservations.map(({ currentObservation, previousObservation }) => (
              <LaborObservationDiff
                key={currentObservation.id}
                currentObservation={currentObservation}
                previousObservation={previousObservation}
                title={
                  currentObservation.code?.coding?.[0]?.display || "Newborn Postpartum Observation"
                }
              />
            ))}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};

export default PostpartumDiff;
